'use client';

import { useMemo, useRef } from 'react';

import { Accordion } from '@flipster/features/src/shared/faq-accordion';
import { useGetUrlWithAnonymousId } from '@flipster/features/src/shared/hooks/useGetUrlWithAnonymousId';
import { useCheckSpotOnly } from '@flipster/features/src/shared/regulation';
import { zendesk } from '@flipster/features/src/shared/zendesk/url';
import { useZendesk } from '@flipster/features/src/shared/zendesk/use-zendesk';
import { Button, TextButton } from '@flipster/react/design-system';
import { IsomorphicLink } from '@flipster/react/design-utils/isomorphic-link';
import { Styles, css, cx } from '@flipster/styled-system/css';
import { center } from '@flipster/styled-system/patterns';
import { richText as richTextRecipe } from '@flipster/styled-system/recipes';
import segment from '@flipster/utils/segment';
import { MainEvent } from '@flipster/utils/segment/events/main';

import { maxWStylesWithMargin, sectionLargeHeadingStyles } from '@web/app/[locale]/(web)/_main-page/_styles';
import { useMainPageIntersectionSegment } from '@web/app/[locale]/(web)/_main-page/hooks/useIntersectionSegment';
import { YoutubeIcon } from '@web/assets/svgs/main-page';

export const Faq = ({ css: cssProp }: { css?: Styles }) => {
  const intersectionRef = useRef<HTMLHeadingElement>(null);
  useMainPageIntersectionSegment(intersectionRef, 'Frequently asked questions');
  const { openMessenger } = useZendesk();
  const isSpotOnly = useCheckSpotOnly();
  const getUrlWithAnonymousId = useGetUrlWithAnonymousId();
  const recipe = richTextRecipe({ style: 'custom' });

  const Contents = useMemo(
    () => [
      {
        title: 'What is Flipster?',
        desc: (
          <>
            <p>
              <strong>
                Flipster is a high-performance crypto perpetuals trading platform engineered by traders, optimized for
                trading.
              </strong>
            </p>
            <p>
              We deliver <strong>zero-friction trading:</strong> zero spreads on major perp pairs, ultra-fast execution,
              and precision order fills the moment you click. It’s a level of performance and cost-efficiency rarely
              seen, built for those who demand more from every trade.
            </p>
            <p>
              <strong>Earn while you trade</strong> — passively earn yield on your USDT balance even as you stay active
              in the market. No missed opportunities. Just full-spectrum capital efficiency.
            </p>
          </>
        ),
      },
      {
        title: 'How to sign up on Flipster?',
        desc: (
          <>
            {'You can '}
            <IsomorphicLink href='/signup' newTab>
              register
            </IsomorphicLink>
            {' for a Flipster account in three easy steps:'}
            <ol>
              <li>
                {'Go to the '}
                <IsomorphicLink href='/'>Flipster website</IsomorphicLink>
                {' or download the Flipster app ('}
                <IsomorphicLink href={getUrlWithAnonymousId('WebFaq')} newTab>
                  iOS
                </IsomorphicLink>
                {' or '}
                <IsomorphicLink href={getUrlWithAnonymousId('AndroidFaq')} newTab>
                  Android
                </IsomorphicLink>
                ).
              </li>
              <li>
                <IsomorphicLink href='/signup' newTab>
                  Sign up
                </IsomorphicLink>
                {' for an account on the Flipster website or within the Flipster app.'}
              </li>
              <li>
                {'Complete '}
                <IsomorphicLink href={zendesk.getUrl('HowToVerifyYourFlipsterAccount')} newTab>
                  identity verification
                </IsomorphicLink>
              </li>
            </ol>
            <p>
              <strong>Note:</strong> We recommend that you check your{' '}
              <IsomorphicLink href={zendesk.getUrl('RestrictedCountries')} newTab>
                country's eligibility
              </IsomorphicLink>
              {
                ' before you start, as eligibility can change over time. Be sure to visit the eligibility page in the future to see if your country has been removed from the restriction list.'
              }
            </p>
            <p>
              {
                'You must complete your identity verification to unlock all the features in your Flipster account, such as trading, deposit and withdrawal. If you encounter any issues during the process, you can contact customer support by '
              }
              <IsomorphicLink href={zendesk.getUrl('RequestNew')} newTab>
                submitting an enquiry
              </IsomorphicLink>
              , or via our 24/7 live chat.
            </p>
          </>
        ),
        youtube: (
          <>
            <TextButton
              color='accent'
              size='large'
              as='a'
              href='https://www.youtube.com/watch?v=OVtG2aA1emI'
              newTab
              icon={<YoutubeIcon />}
              label='How to Sign Up on Flipster | Step by Step Guide'
            />
            <br />
            <TextButton
              color='accent'
              size='large'
              as='a'
              href='https://www.youtube.com/watch?v=0JOvJJOldzk'
              newTab
              icon={<YoutubeIcon />}
              label='How to Complete Identity Verification (KYC) on Flipster | Step by Step Guide'
            />
          </>
        ),
      },
      {
        title: 'How to earn crypto rewards on Flipster?',
        desc: (
          <>
            <p>
              {'Flipster supports its community during all '}
              <IsomorphicLink href='/blog/4-stages-crypto-market-cycle' newTab>
                market cycles
              </IsomorphicLink>
              {
                ' by offering various initiatives that enhance the trading experience. Users can explore numerous opportunities to participate in our events, promotions, and trading competitions, and gain rewards through our '
              }
              <IsomorphicLink href='/marketing/rewards-hub' newTab>
                Rewards Hub
              </IsomorphicLink>
              {', '}
              <IsomorphicLink href='/marketing/referral' newTab>
                Referral
              </IsomorphicLink>
              {', and '}
              <IsomorphicLink href='/marketing/vip' newTab>
                VIP
              </IsomorphicLink>
              {' Programs.'}
            </p>
            <p className={css({ fontWeight: 'bold', color: 'content.default.level-4' })}>
              Trade and earn simultaneously
            </p>
            <p>
              {'To support our users in maximizing their funds during a bear market, we introduced the '}
              <IsomorphicLink href='/earn' newTab>
                Earn Campaign
              </IsomorphicLink>
              {'. This campaign allows traders to earn passive rewards with a high '}
              <IsomorphicLink href='/glossary/annual-percentage-rate-apr' newTab>
                APR
              </IsomorphicLink>
              {' on the USDT, BTC, and ETH wallet balance in their Flipster accounts.'}
            </p>
            <p>
              {
                'A key advantage is users can earn APR rewards on their USDT balance while actively trading derivatives at the same time, all from the same wallet. As they strategize on their next move, users can be ready to act quickly when market opportunities arise, avoiding delays from making '
              }
              <IsomorphicLink href='/support/guide/perpetual-swaps' newTab>
                swaps
              </IsomorphicLink>
              , depositing fresh funds, or transferring crypto.
            </p>
          </>
        ),
      },
      {
        title: "What are Flipster's trading fees?",
        desc: (
          <>
            <p>
              {'Flipster uses a '}
              <IsomorphicLink href='/support/guide/fees' newTab>
                tiered trading fee system
              </IsomorphicLink>
              {' with ultra-low, transparent trading fees — the more you trade, the more you save.'}
            </p>
            <p>
              Starting from just 0.025%, our tiered fee structure rewards high-volume traders with even lower rates.
              Whether you’re scalping or swinging, every trade benefits from deeper liquidity, tighter spreads, and
              near-zero slippage — so you keep more of what you earn.
            </p>
            <p>
              With zero-spread on 20+ major perpetual pairs, you get cleaner entries, sharper exits, and better
              execution — even in fast-moving markets. There’s no guesswork, no hidden costs — just precision pricing
              that moves with you.
            </p>
          </>
        ),
      },
      {
        title: 'Why choose Flipster to trade altcoin derivatives?',
        desc: (
          <>
            <p>
              Flipster offers a structural edge for altcoin derivative traders by minimizing hidden costs and optimizing
              execution:
            </p>
            <ul>
              <li>
                <strong>Zero spreads on 20+ major perp pairs:</strong> Unlike most platforms that quote separate bid/ask
                prices, Flipster provides a single, unified price, removing spread costs that can quietly erode returns,
                especially on volatile or less liquid altcoins.
              </li>
              <li>
                <IsomorphicLink href='blog/how-flipster-stacks-up-real-trading-costs-across-top-perps-exchanges' newTab>
                  Lowest all-in trading costs:
                </IsomorphicLink>
                {' While some exchanges may post lower fees at top-tier VIP levels, these are often offset by larger'}
                spreads and slippage. Flipster’s zero-friction execution model delivers lower total cost per trade
                across all tiers and sizes.
              </li>
              <li>
                <strong>Transparent, predictable pricing:</strong> With tier-based fee discounts and flat, visible
                rates, traders benefit from a clear cost curve that scales with volume, without execution surprises.
              </li>
            </ul>
            <p>
              This matters most when trading altcoins like major altcoins perps, where spread and slippage can often
              exceed the posted fee. Flipster is designed to keep those costs low and consistent, so traders can operate
              with precision.
            </p>
          </>
        ),
      },
    ],
    [getUrlWithAnonymousId],
  );

  const SpotOnlyContents = useMemo(
    () => [
      {
        title: 'What is Flipster?',
        desc: (
          <p>
            Flipster is the leading cryptocurrency trading platform. Traders rely on our app to gain a competitive edge,
            turning their insights and knowledge into potential profits. Discover a new way to trade cryptocurrencies
            and join a thriving community of traders who return to our platform time and again.
          </p>
        ),
      },
      {
        title: 'How has Flipster optimized the trading experience?',
        desc: (
          <>
            <p>
              Flipster offers an optimized user experience across both mobile and web platforms, featuring seamless
              cross-platform integration. This allows users to easily trade cryptos and analyze charts on the web while
              staying updated on the go through the mobile app (
              <IsomorphicLink href={getUrlWithAnonymousId('WebFaq')} newTab>
                iOS
              </IsomorphicLink>
              {' or '}
              <IsomorphicLink href={getUrlWithAnonymousId('AndroidFaq')} newTab>
                Android
              </IsomorphicLink>
              ).
            </p>
            <p>
              Recognizing that today's traders aren't tied to their desktops, we've redesigned our trading platform to
              be user-friendly and intuitive, moving away from the complex interfaces of traditional financial
              platforms.
            </p>
            <p>
              Flipster's app and website are built for speed and execution, ensuring users can enjoy fast, frictionless
              trades, track portfolios, and take advantage of market opportunities—anytime, anywhere.
            </p>
          </>
        ),
      },
      {
        title: 'How to get started on Flipster?',
        desc: (
          <>
            <p>
              {'You can '}
              <IsomorphicLink href='/support/guide/registration' newTab>
                register
              </IsomorphicLink>
              {' for a Flipster account in three easy steps.'}
            </p>
            <ol>
              <li>
                {'Go to the '}
                <IsomorphicLink href='/'>Flipster website</IsomorphicLink>
                {' or download the Flipster app '}(
                <IsomorphicLink href={getUrlWithAnonymousId('WebFaq')} newTab>
                  iOS
                </IsomorphicLink>
                {' or '}
                <IsomorphicLink href={getUrlWithAnonymousId('AndroidFaq')} newTab>
                  Android
                </IsomorphicLink>
                ).
              </li>
              <li>
                <IsomorphicLink href='/signup' newTab>
                  Sign up
                </IsomorphicLink>
                {' for an account on the Flipster website or within the Flipster app.'}
              </li>
              <li>
                {'Complete '}
                <IsomorphicLink href={zendesk.getUrl('HowToVerifyYourFlipsterAccount')} newTab>
                  identity verification
                </IsomorphicLink>
                .
              </li>
            </ol>
            <p>
              Note for UAE residents: Flipster is committed to providing the safest and most seamless trading experience
              in accordance with the regulations set by the Virtual Assets Regulatory Authority (VARA). By aligning with
              local compliance standards, we ensure that UAE users can access our platform with confidence and ease.
            </p>
            <p>
              {
                'You must complete your identity verification to unlock all the features in your Flipster account, such as trading, deposit and withdrawal. If you encounter any issues during the process, you can contact customer support by '
              }
              <IsomorphicLink href={zendesk.getUrl('RequestNew')} newTab>
                submitting an enquiry
              </IsomorphicLink>
              , or via our 24/7 live chat.
            </p>
          </>
        ),
        youtube: (
          <TextButton
            color='accent'
            size='large'
            as='a'
            href='https://www.youtube.com/watch?v=OVtG2aA1emI'
            newTab
            icon={<YoutubeIcon />}
            label='How to Sign Up for a Flipster Account?'
          />
        ),
      },
      {
        title: 'How to register an account on the Flipster app?',
        desc: (
          <ol>
            <li>
              {'Download the Flipster app (iOS or Android) . You can find us in the '}
              <IsomorphicLink href={getUrlWithAnonymousId('AndroidFaq')} newTab>
                Google Play
              </IsomorphicLink>
              {' or '}
              <IsomorphicLink href={getUrlWithAnonymousId('WebFaq')} newTab>
                Apple App Store
              </IsomorphicLink>
              .
            </li>
            <li>Sign up with your email address, Google account, Apple ID or via Social sign-on (SSO).</li>
            <li>Complete identity verification.</li>
          </ol>
        ),
        youtube: (
          <TextButton
            color='accent'
            size='large'
            as='a'
            href='https://www.youtube.com/watch?v=0JOvJJOldzk'
            newTab
            icon={<YoutubeIcon />}
            label='How to Complete Identity Verification on Flipster?'
          />
        ),
      },
      {
        title: 'How to trade crypto on Flipster?',
        desc: (
          <ol>
            <li>
              On the mobile application, go to the [Assets] page. Click on [Receive crypto] to deposit cryptocurrencies
              to your Flipster account from an external wallet.
            </li>
            <li>
              Under the Trade tab, select an asset you wish to trade. Next, enter the amount of asset you wish to buy or
              sell before clicking [Review your order].
            </li>
            <li>Check all the details before tapping buy or sell.</li>
          </ol>
        ),
        youtube: (
          <TextButton
            color='accent'
            size='large'
            as='a'
            href='https://www.youtube.com/watch?v=SKm9i7AVIYw'
            newTab
            icon={<YoutubeIcon />}
            label='Trade on Flipster'
          />
        ),
      },
      {
        title: 'How to deposit crypto to Flipster?',
        desc: (
          <>
            <ol>
              <li>
                On the mobile app, click on [Asset], followed by [Deposit crypto]. Alternatively, if you're on the
                website, go to the [Wallet] menu and select [Receive Crypto].
              </li>
              <li>
                {'Select the cryptocurrency asset you wish to '}
                <IsomorphicLink href='/blog/how-to-deposit-crypto-to-flipster' newTab>
                  deposit
                </IsomorphicLink>
                , and the deposit network. A network and a wallet address will be generated.
              </li>
              <li>
                Scan the QR code or copy and paste the wallet address manually using your crypto wallet or external
                platform, to send crypto to your Flipster wallet.
              </li>
            </ol>
            <p>Note:</p>
            <ul>
              <li>
                {'Flipster does not charge any deposit '}
                <IsomorphicLink href='/support/guide/fees' newTab>
                  fees
                </IsomorphicLink>
                , but you may incur network fees when transferring funds from an external wallet to your Flipster
                wallet.
              </li>
              <li>
                {
                  "If you select an incompatible or different deposit network, you might lose your funds. Please check the contract address of the asset you're depositing, which must be the same as the one displayed, to ensure your "
                }
                <IsomorphicLink href='/support/guide/assets' newTab>
                  assets
                </IsomorphicLink>
                {' get deposited.'}
              </li>
              <li>
                Transactions can take some time to be confirmed, depending on the network traffic. Be patient, you'll
                see the funds in your Flipster account shortly. We'll also send you an email once the funds have been
                deposited on Flipster.
              </li>
              <li>You may monitor the deposit status by going to the [History] - [Deposit] page.</li>
            </ul>
          </>
        ),
        youtube: (
          <>
            <TextButton
              color='accent'
              size='large'
              as='a'
              href='https://www.youtube.com/watch?v=JhqmnI-8lqE'
              newTab
              icon={<YoutubeIcon />}
              label='How to Deposit on the Flipster App'
            />
            <br />
            <TextButton
              color='accent'
              size='large'
              as='a'
              href='https://www.youtube.com/watch?v=0WsA0BOCF4E'
              newTab
              icon={<YoutubeIcon />}
              label='How to Deposit Crypto on the Flipster Web'
            />
          </>
        ),
      },
    ],
    [getUrlWithAnonymousId],
  );

  const faqContents = useMemo(
    () => (isSpotOnly ? SpotOnlyContents : Contents),
    [Contents, SpotOnlyContents, isSpotOnly],
  );

  return (
    <section className={css(maxWStylesWithMargin, cssProp)}>
      <h2 ref={intersectionRef} className={css(sectionLargeHeadingStyles, { mb: { base: 32, tablet: 40 } })}>
        Frequently asked questions
      </h2>

      <Accordion.Root headingLevel={2} css={{ mb: { base: 32, tablet: 40 } }}>
        {faqContents.map((content, index) => (
          <Accordion.Item key={content.title}>
            <Accordion.Header index={index + 1}>{content.title}</Accordion.Header>
            <Accordion.Panel>
              <div className={cx(recipe, css({ textStyle: 'Text/L/Regular', color: 'content.default.level-2' }))}>
                {content.desc}
              </div>
              {content.youtube && <div className={css({ mt: 22 })}>{content.youtube}</div>}
            </Accordion.Panel>
          </Accordion.Item>
        ))}
      </Accordion.Root>

      <div className={center()}>
        <Button
          hierarchy='primary'
          color='accent'
          size='medium'
          label='More questions?'
          onClick={() => {
            segment.track(MainEvent.MainPageChatClick);
            openMessenger();
          }}
          css={{ minW: 154 }}
        />
      </div>
    </section>
  );
};
